import { useActiveVkuiLocation } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { useSession } from '../../api/session';
import {
  ROLE_LABELS,
  type MyAccountResponse,
  type MySettingsResponse,
  type SessionUser,
} from '../../api/types';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { ExternalIcon } from '../../shared/icons';
import { digitGroups } from '../../shared/lib/docNumber';
import {
  Bullet,
  Card,
  CardHeading,
  CardList,
  DefList,
  ErrorState,
  ExternalLink,
  Gap,
  Gutter,
  LoadingState,
  Muted,
  Notice,
  Prose,
  Screen,
  Section,
  SectionTitle,
  SettingRow,
  StatusBadge,
  Toggle,
  type StatusTone,
} from '../../shared/ui';
import { vkProfileUrl } from '../review/staffKit';

/**
 * Профиль: то немногое, что игрок может настроить в самом приложении.
 *
 * Настройка здесь ровно одна, и она про приватность, а не про оформление:
 * реестр открыт ЛЮБОМУ пользователю ВК, который открыл приложение, — в том
 * числе тому, кого нет в списке участников. Значит, ссылка «кто играет за
 * этого персонажа» по умолчанию видна посторонним, и человек должен иметь
 * возможность её убрать, не спрашивая администрацию.
 *
 * Значение живёт на аккаунте (users.hide_vk_profile), а не на персонаже:
 * скрывается сам игрок, а не отдельная карточка. Читаем и пишем его через
 * GET/PATCH /api/users/me — настройки аккаунта отдаёт модуль users, владелец
 * таблицы users. В ответе кабинета (/api/characters/me) этого поля нет.
 */

/**
 * Из ответа GET /api/users/me этому экрану нужен ровно один флаг. Счётчик
 * непрочитанных решений приезжает в том же ответе, но читают его таббар и хаб
 * персонажа, а не профиль.
 *
 * Форма ответа описана в api/types.ts: GET отдаёт MyAccountResponse,
 * PATCH — только MySettingsResponse. Её читают
 * три экрана, и трём копиям одного контракта разъезжаться нельзя. Фолбэка при
 * разборе тут быть не должно — он маскировал бы расхождение с сервером, а цена
 * ошибки высокая: тумблер показал бы «выключено», человек нажал бы ещё раз и
 * своими руками вернул ссылку на свой профиль в открытый реестр.
 */
interface ProfileState {
  hideVkProfile: boolean;
}

const TOGGLE_LABEL = t('user.profile.hide.label');

export function ProfilePanel({ id }: NavIdProps) {
  const back = useBack();
  const session = useSession();
  const { panel } = useActiveVkuiLocation();
  const active = id !== undefined && panel === id;

  const user = session.user;
  // Гостю и аккаунту с закрытой записью сервер не отдаст ни чтение, ни запись
  // этой настройки: оба маршрута требуют права записи.
  const canEdit = user !== null && user.canWrite;

  const state = useLoader<ProfileState>(
    async (signal) => {
      const response = await api.get<MyAccountResponse>('/api/users/me', undefined, signal);
      return { hideVkProfile: response.hideVkProfile };
    },
    [],
    active && canEdit,
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function toggle(next: boolean) {
    setSaving(true);
    setSaveError(null);
    // Переключатель двигаем сразу: ждать ответа сервера, глядя на неподвижный
    // тумблер, человек не станет — нажмёт второй раз.
    state.replace({ hideVkProfile: next });
    try {
      const response = await api.patch<MySettingsResponse>('/api/users/me', {
        hideVkProfile: next,
      });
      state.replace({ hideVkProfile: response?.hideVkProfile ?? next });
    } catch (cause: unknown) {
      state.replace({ hideVkProfile: !next });
      setSaveError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  const hidden = state.data?.hideVkProfile ?? false;

  const access: { tone: StatusTone; label: string } =
    user === null
      ? { tone: 'archived', label: t('user.state.readOnly') }
      : user.canWrite
        ? { tone: 'published', label: t('common.access.granted') }
        : { tone: 'rejected', label: t('common.access.revoked') };

  return (
    <Screen id={id} title={t('user.profile.title')} back={back}>
      <Section>
        <SectionTitle>{t('user.profile.section.account')}</SectionTitle>
        <CardList>
          {/* Строками, а не плитками: значений тут два, и одно из них —
              ссылка, а другое у гостя разворачивается в целое предложение. */}
          <Card
            tone="published"
            corner={<StatusBadge tone={access.tone}>{access.label}</StatusBadge>}
          >
            <DefList
              items={[
                {
                  label: t('common.vkProfile'),
                  value: (
                    <ExternalLink href={vkProfileUrl(session.vkUserId)}>
                      {t('user.value.vkId', { id: digitGroups(session.vkUserId) })}{' '}
                      <ExternalIcon size={10} tone="accent" />
                    </ExternalLink>
                  ),
                },
                {
                  label: t('user.field.role'),
                  value: user ? ROLE_LABELS[user.role] : t('user.profile.guestRole'),
                },
              ]}
            />
          </Card>
        </CardList>
      </Section>

      <Section>
        <SectionTitle>{t('user.profile.section.registryLink')}</SectionTitle>
        <Gutter>
          <Prose>{t('user.profile.registryLinkIntro')}</Prose>
        </Gutter>

        {canEdit && state.loading && state.data === null ? (
          <LoadingState text={t('user.profile.loading')} inline />
        ) : null}

        {canEdit && state.error !== null && state.data === null ? (
          <ErrorState message={state.error} onRetry={state.reload} />
        ) : null}

        {canEdit && state.data !== null ? (
          <>
            <CardList>
              <Card tone="published">
                <SettingRow
                  control={
                    <Toggle
                      checked={hidden}
                      // Ответ перечитывания, пришедший позже нажатия, вернул бы тумблер назад.
                      disabled={saving || state.loading}
                      label={TOGGLE_LABEL}
                      onChange={(next) => void toggle(next)}
                    />
                  }
                >
                  <CardHeading>{TOGGLE_LABEL}</CardHeading>
                  <Gap step="s1h" />
                  <Bullet size="ui" tone={hidden ? 'cyan' : 'pink'}>
                    <Muted>{hidden ? t('user.profile.hide.on') : t('user.profile.hide.off')}</Muted>
                  </Bullet>
                </SettingRow>
              </Card>
            </CardList>

            {/* В тех же полях, что карточка над ней: во всю ширину экрана
                плашка читалась как подвал, а не как примечание к настройке. */}
            <Gutter>
              <Notice tone="warning">{t('user.profile.hide.scope')}</Notice>
            </Gutter>
          </>
        ) : null}

        {saveError !== null ? (
          <Gutter>
            <Notice tone="error" title={t('user.profile.saveError')}>
              {saveError}
            </Notice>
          </Gutter>
        ) : null}

        {!canEdit ? (
          <Gutter>
            <Notice tone="info" title={t('user.profile.locked.title')}>
              {lockedText(user)}
            </Notice>
          </Gutter>
        ) : null}
      </Section>
    </Screen>
  );
}

function lockedText(user: SessionUser | null): string {
  if (user === null) return t('user.profile.locked.guest');
  return user.deletedAt === null
    ? t('user.profile.locked.readOnly')
    : t('user.profile.locked.deleted');
}
